"use client";

import Linkify from "linkify-react";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { isRichNote, renderNoteHtml } from "./note-format";

const LINK_CLASS = "text-primary dark:text-brand-accent underline";

/**
 * Renders a comment body. Rich notes (saved as HTML by the note composer)
 * render through the note schema; plain-text comments keep their line
 * breaks and get bare URLs linked. Both paths share the same type scale so
 * a thread with a mix of old and new notes reads evenly.
 */
export function CommentBody({ body, className }: { body: string; className?: string }) {
  const rich = isRichNote(body);
  const html = useMemo(() => (rich ? renderNoteHtml(body) : ""), [rich, body]);

  if (rich) {
    return (
      <div
        className={cn(
          "prose prose-sm dark:prose-invert max-w-none break-words text-sm leading-relaxed text-foreground",
          "[&_p]:my-0 [&_p+p]:mt-2 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0 [&_blockquote]:my-1 [&_pre]:my-1 [&_hr]:my-2 [&_strong]:text-inherit [&_code]:text-inherit",
          className,
        )}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: markup is regenerated from the note schema in renderNoteHtml
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <p
      className={cn(
        "whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground",
        className,
      )}
    >
      <Linkify options={{ className: LINK_CLASS, target: "_blank", rel: "noopener noreferrer" }}>
        {body}
      </Linkify>
    </p>
  );
}
